import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { type VariantProps } from 'class-variance-authority'

import { cn } from '@/common/utils/utils'
import { Spinner } from './spinner'
import { buttonVariants } from './buttonVariants'

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean
    focused?: boolean
    loading?: boolean
    innerClassName?: string
    midClassName?: string
    fullWidth?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            focused,
            loading,
            innerClassName,
            midClassName,
            size,
            asChild = false,
            fullWidth = false,
            ...props
        },
        ref,
    ) => {
        const Comp = asChild ? Slot : 'button'
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }), {
                    'ring-2 ring-secondary-light': focused,
                    'cursor-not-allowed': loading,
                    'w-full': fullWidth,
                })}
                ref={ref}
                {...props}
                disabled={loading || props.disabled}
            >
                <div className={cn('flex gap-2 items-center justify-center', midClassName)}>
                    <span
                        className={cn(
                            'inline-flex items-center justify-center whitespace-nowrap gap-2',
                            innerClassName,
                        )}
                    >
                        {props.children}
                    </span>
                    {loading && <Spinner />}
                </div>
            </Comp>
        )
    },
)
Button.displayName = 'Button'

export { Button }
